import React, { FC, useState, useEffect, useId, useCallback } from 'react';
import DeleteTaskDialog from './DeleteTaskDialog';
import AddNewTaskDialog from './AddNewTaskDialog';
import axiosInstance from 'config/axiosInstance';
import { fetchAllPages } from 'config/api';
import { useTranslation } from 'react-i18next';
import i18n from 'config/i18n';
import { useModalDialog } from 'reusable/useModalDialog';
import FetchFailed from 'reusable/FetchFailed';
import { useLatestRequest } from 'reusable/useLatestRequest';
import { buttonLike } from 'reusable/buttonLike';
import { secondsToHMS, totalSeconds } from 'reusable/Client/totalSeconds';
import { durationText } from 'reusable/durationText';

interface Task {
  id: string;
  is_active: boolean;
  name: string;
  icon: string;
  value: string;
  client_delivery_count: string;
  total_hours_spent: string;
}

interface ManageTaskDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
}

// ADM-16。単位は表示の言語で出す（以前は「2 hrs 5 mins」の英語の固定文字）。
// EV-299。配送先から作業を外す確認と同じ時・分・秒（既存の文言）で出す。以前は分を切り上げていた。
function convertSeconds(seconds: number): string {
  // EV-312。値が数でないと英語の入れ子の文言が崩れるため、共通の関数で「-」にする。
  return durationText((key, options) => i18n.t(key, options), 'remove_task.duration_value', { ...secondsToHMS(seconds) }, { ns: 'client' });
}

const ManageTaskDialog: FC<ManageTaskDialogProps> = ({
  isOpen,
  onClose,
  title,
}) => {
  const { t } = useTranslation('client');
  const [tasks, setTasks] = useState<Task[]>();
  const [tasksCount, setTasksCount] = useState(0);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isTaskRemovalDialogOpen, setIsTaskRemovalDialogOpen] = useState(false);
  const [isAddNewTaskDialogOpen, setIsAddNewTaskDialogOpen] = useState(false);
  const [selectedTaskData, setSelectedTaskData] = useState<Task | null>(null);

  const beginList = useLatestRequest();
  const beginSummary = useLatestRequest();
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<unknown>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryFailure, setSummaryFailure] = useState<{ task: Task; error: unknown } | null>(null);

  const fetchData = useCallback(async () => {
    const isCurrent = beginList();
    setLoading(true); setFetchError(null);
    try {
      const items = await fetchAllPages<Task>('/v1/tasks/');
      if (!isCurrent()) return undefined;
      setTasks(items); setTasksCount(items.length);
      // EV-129。結果不明後の同名確認へ、取得できた全件を返す契約を維持する。
      return items;
    } catch (error) {
      // EV-151。失敗したら前回の一覧を残さない（失敗の表示と古い一覧が並んでいた）。
      if (isCurrent()) { setFetchError(error); setTasks(undefined); setTasksCount(0); }
      return undefined;
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [beginList]);

  useEffect(() => {
    if (isOpen) {
      setIsRemoving(false); setIsTaskRemovalDialogOpen(false); setIsAddNewTaskDialogOpen(false);
      setSummaryFailure(null); setSummaryLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !isTaskRemovalDialogOpen && !isAddNewTaskDialogOpen) fetchData();
    return () => { beginList(); beginSummary(); };
  }, [isOpen, isTaskRemovalDialogOpen, isAddNewTaskDialogOpen, fetchData, beginList, beginSummary]);

  const handleCancelRemove = () => {
    beginSummary(); setSummaryFailure(null); setSummaryLoading(false);
    setIsRemoving(!isRemoving);
  };

  const handleTaskRemovalClick = async (task: Task) => {
    const isCurrent = beginSummary();
    setSummaryLoading(true); setSummaryFailure(null);
    try {
      const response = await axiosInstance.get(`/v1/tasks/${task.id}/summarize/`);
      if (!isCurrent()) return;
      // EV-288。数値の秒（新しい API）も、秒の文字列・null（古い API）も同じ秒として読む。
      setSelectedTaskData({ ...response.data, total_hours_spent: convertSeconds(totalSeconds(response.data.total_hours_spent)) });
      setIsTaskRemovalDialogOpen(true);
    } catch (error) {
      if (isCurrent()) setSummaryFailure({ task, error });
    } finally {
      if (isCurrent()) setSummaryLoading(false);
    }
  };

  const handleTaskRemovalDialogClose = () => {
    setIsTaskRemovalDialogOpen(false);
  };

  const handleAddNewTaskClick = () => {
    setIsAddNewTaskDialogOpen(true);
  };

  const handleAddNewTaskDialogClose = () => {
    setIsAddNewTaskDialogOpen(false);
  };

  // ADM-16。Escape で閉じる・フォーカスを中に留める・閉じたら開いた操作へ戻す。
  // RV-125。見出しの id は文書の中で一意にする（重なったダイアログが別の見出しで読まれた）。
  const headingId = useId();
  const dialogRef = useModalDialog(isOpen, onClose);

  if (!isOpen) return null;

  return (
    <>
      {/* EV-117。中のダイアログを開く間も外側は外さずに隠す（外して作り直すと、フォーカスの
          戻り先（開いたボタン）が無くなり、作り直した要素に Escape・Tab の処理も付かなかった）。 */}
      {(
        <div
          className="fixed z-10 inset-0 overflow-y-auto"
          hidden={isTaskRemovalDialogOpen || isAddNewTaskDialogOpen}
          aria-hidden={isTaskRemovalDialogOpen || isAddNewTaskDialogOpen || undefined}
        >
          <div className="flex items-center justify-center min-h-screen px-4 py-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              aria-hidden="true"
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div
              className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 w-[720px]"
              role="dialog"
              ref={dialogRef}
              aria-modal="true"
              aria-labelledby={headingId}
              tabIndex={-1}
            >
              <div className="bg-white">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-left sm:mt-0 w-full">
                    <div className="flex justify-between items-center mt-4 mx-6">
                      <h3
                        className="text-subtitle-1 font-medium text-neutral/300"
                        id={headingId}
                      >
                        {title}
                      </h3>
                      <button type="button"
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-500 focus:outline-none"
                      >
                        <span className="sr-only">{t('ui.close', { ns: 'common' })}</span>
                        <svg
                          className="h-6 w-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                    <hr className="border-gray-300 mt-4 w-full" />
                    <form>
                      {(loading || summaryLoading) && <p role="status">{t('ui.loading', { ns: 'common' })}</p>}
                      {fetchError ? <FetchFailed error={fetchError} onRetry={fetchData} /> : null}
                      {summaryFailure ? <FetchFailed error={summaryFailure.error} onRetry={() => handleTaskRemovalClick(summaryFailure.task)} message={t('ui.detail-fetch-failed', { ns: 'common' })} /> : null}
                      {!isRemoving ? (
                        tasksCount < 15 ? (
                          <button
                            type="button"
                            className="rounded-md bg-surface-card border-primary/300 border-2 border-dashed box-border h-12 w-[672px] flex flex-row items-center justify-center gap-2 mt-6 mx-6"
                            onClick={handleAddNewTaskClick}
                            disabled={loading || Boolean(fetchError)}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width={24}
                              height={24}
                              viewBox="0 0 48 48"
                            >
                              <path
                                fill="none"
                                stroke="#30537E"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={3}
                                d="m24.06 10l-.036 28M10 24h28"
                              ></path>
                            </svg>
                            <div className="h-6 flex flex-row items-center justify-center">
                              <span className="relative text-primary/300 font-semi-bold text-subtitle-2">
                                {t('manage_task.create_new_task')}
                              </span>
                            </div>
                          </button>
                        ) : (
                          <span className="flex flex-row items-center justify-center h-12 mt-6 text-neutral/75 font-semi-bold text-subtitle-2">
                            {t('manage_task.max_task')}
                          </span>
                        )
                      ) : (
                        <span className="flex flex-row items-center justify-center h-12 mt-6 mx-6">
                          {t('manage_task.remove_task_caption')}
                        </span>
                      )}
                      <div className="grid grid-cols-3 gap-x-2 gap-y-4 p-6">
                        {!fetchError && tasks?.map((task, index) => (
                          <TaskItem
                            key={index}
                            label={task.name}
                            icon={task.icon}
                            isRemoving={isRemoving}
                            disabled={loading || Boolean(fetchError) || summaryLoading}
                            onRemove={() => handleTaskRemovalClick(task)}
                          />
                        ))}
                      </div>
                      <div className="px-6 pb-4">
                        <button
                          type="button"
                          className="inline-flex justify-center items-center rounded-md font-medium text-l text-error gap-2"
                          onClick={handleCancelRemove}
                          // EV-152。削除の開始は取得中・失敗中は無効のまま、削除モードの取消は常に押せる。
                          disabled={!isRemoving && (loading || Boolean(fetchError))}
                        >
                          {!isRemoving ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 48 48"
                            >
                              <g fill="none" stroke="#de0000" strokeWidth="3">
                                <path
                                  strokeLinejoin="round"
                                  d="m15 12l1.2-7h15.6l1.2 7"
                                />
                                <path strokeLinecap="round" d="M6 12h36" />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="m37 12l-2 31H13l-2-31z"
                                  clipRule="evenodd"
                                />
                                <path strokeLinecap="round" d="M19 35h10" />
                              </g>
                            </svg>
                          ) : (
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M5.83301 5.8335L18.1663 18.1668M18.1663 5.8335L5.83301 18.1668"
                                stroke="#DE0000"
                                strokeWidth="1.5"
                                strokeMiterlimit="10"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                          {!isRemoving
                            ? t('manage_task.remove_task')
                            : t('action.cancel')}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isTaskRemovalDialogOpen && selectedTaskData && (
        <DeleteTaskDialog
          taskName={selectedTaskData.name}
          isOpen={isTaskRemovalDialogOpen}
          onClose={handleTaskRemovalDialogClose}
          fields={[
            {
              label: t('delete_task.task_name'),
              value: selectedTaskData.name,
            },
            {
              label: t('delete_task.total_client'),
              value: selectedTaskData.client_delivery_count || '0',
            },
            {
              label: t('delete_task.total_duration'),
              value: selectedTaskData.total_hours_spent || '0',
            },
          ]}
          taskId={selectedTaskData.id}
        />
      )}

      {isAddNewTaskDialogOpen && (
        <AddNewTaskDialog
          isOpen={isAddNewTaskDialogOpen}
          onClose={handleAddNewTaskDialogClose}
          onReload={fetchData}
        />
      )}
    </>
  );
};

interface TaskItemProps {
  label: string;
  icon: React.ReactNode;
  isRemoving: boolean;
  onRemove: () => void;
  disabled?: boolean;
}

const TaskItem: FC<TaskItemProps> = ({ label, icon, isRemoving, onRemove, disabled }) => {
  const [iconSrc, setIconSrc] = useState<string | null>(null);

  useEffect(() => {
    const loadIcon = async () => {
      try {
        const importedIcon = await import(
          `../../../assets/icons/tasks/${icon}.svg`
        );
        setIconSrc(importedIcon.default);
      } catch (err) {
        console.error(`Icon not found: ${icon}`, err);
        setIconSrc(null);
      }
    };

    loadIcon();
  }, [icon]);

  const { t } = useTranslation('client');
  const handleRemoveClick = () => {
    if (!disabled) onRemove();
  };

  return !isRemoving ? (
    <div
      className={`flex flex-row items-center justify-between px-2 h-16 border rounded-md shadow-sm w-full`}
    >
      <span className="text-neutral/300 text-body-2">{label}</span>

      {iconSrc && (
        <img src={iconSrc} alt={`${label} icon`} className="w-8 h-8" />
      )}
    </div>
  ) : (
    <div className="h-16 rounded-lg justify-start items-center gap-1 inline-flex">
      <div className="h-full w-full px-2 bg-error/10 rounded-tl-lg rounded-bl-lg border border-error/40 justify-start items-center gap-2 flex">
        <div className="self-center text-neutral/300 text-body-2">{label}</div>
      </div>
      <div
        className="w-[38px] h-16 px-2 bg-error rounded-tr-lg rounded-br-lg justify-start items-center gap-1 flex cursor-pointer"
        aria-disabled={disabled || undefined}
        {...buttonLike(handleRemoveClick, `${label} ${t('ui.delete', { ns: 'common' })}`)}
      >
        <div className="w-5 h-5 justify-center items-center flex">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 48 48"
          >
            <g fill="none" stroke="#ffffff" strokeWidth="3">
              <path strokeLinejoin="round" d="m15 12l1.2-7h15.6l1.2 7" />
              <path strokeLinecap="round" d="M6 12h36" />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m37 12l-2 31H13l-2-31z"
                clipRule="evenodd"
              />
              <path strokeLinecap="round" d="M19 35h10" />
            </g>
          </svg>
        </div>
      </div>
    </div>
  );
};

export default ManageTaskDialog;

export const importAllIcons = async () => {
  const iconNames = [
    'leave_for_work',
    'prepare_departure',
    'driving',
    'cargo_bulk',
    'cargo_pallet',
    'exchange_cargo',
    'exchange_cargo_pl',
    'wait_for_cargo',
    'unload_bulk',
    'unload_pallet',
    'rest',
    'wait_for_price_reduction',
    'prepare_to_go_home',
    'return_home',
    'walking',
    'banknotes',
    'battery_50',
    'briefcase',
    'clipboard_document_check',
    'clipboard_document_list',
    'document_check',
    'exclamation_triangle',
    'face_frown',
    'face_smile',
    'finger_print',
    'key',
    'moon',
    'photo',
    'star',
    'truck',
    'user_group',
    'wrench_screwdriver',
    'clock',
    'credit_card',
  ];

  const iconsMap: { [key: string]: string } = {};
  await Promise.all(
    iconNames.map(async (name) => {
      const icon = await import(`../../../assets/icons/tasks/${name}.svg`);
      iconsMap[name] = icon.default;
    })
  );

  return iconsMap;
};
